import { Store } from "@reduxjs/toolkit";
import { RootState } from "src/store";
import { setTranscriptData } from "src/pages/ManageStudies/states/slice/manageTranscriptSlice";
import { setIsEditable, setManageStudyDetails } from "src/pages/ManageStudies/states/slice/manageStudySlice";
import { setUnsavedChanges } from "src/pages/ManageStudies/states/slice/unsavedChangesSlice";
import { sanitizeForLog } from "src/utils/security";
import { message } from "src/assets/imports/antdComponents";
import { getMetaDetails } from "src/pages/Studies/serviceHelper";
import { environment } from "src/config/environment";
import { getWebSocketAuthProtocols } from "src/services/wsAuth";

export interface WebSocketMessage {
	type: string;
	[key: string]: any;
}

export interface TranscriptUpdateData {
	content?: string;
	document_id?: string;
	transcript?: any[];
}

export type WebSocketMessageHandler = (data: any) => void;

class WebSocketTranscriptService {
	private socket: WebSocket | null = null;
	private store: Store | null = null;
	private messageHandlers: Map<string, WebSocketMessageHandler[]> = new Map();
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 120; // 30 minutes at 15 seconds per attempt = 120 attempts
	private reconnectTimeout: NodeJS.Timeout | null = null;
	private lastActivityTime: Date = new Date();
	private heartbeatInterval: NodeJS.Timeout | null = null;
	public currentStudyId: string | null = null;
	private currentFileId: string | null = null;
	private locationChangeListener: (() => void) | null = null;
	private isReconnecting: boolean = false;
	private connectionLostNotificationKey: string | number | null = null;
	private isReconnectedSocket: boolean = false;
	private pendingUpdates: WebSocketMessage[] = [];
	private errorHandled: boolean = false;

	private boundNetworkStatusChange: () => void;
	private networkListenersAttached: boolean = false;

	constructor() {
		this.boundNetworkStatusChange = this.handleNetworkStatusChange.bind(this);
		this.attachNetworkListeners();
	}

	private attachNetworkListeners() {
		if (this.networkListenersAttached) return;
		window.addEventListener("online", this.boundNetworkStatusChange);
		window.addEventListener("offline", this.boundNetworkStatusChange);
		this.networkListenersAttached = true;
	}

	private detachNetworkListeners() {
		if (!this.networkListenersAttached) return;
		window.removeEventListener("online", this.boundNetworkStatusChange);
		window.removeEventListener("offline", this.boundNetworkStatusChange);
		this.networkListenersAttached = false;
	}

	public isReconnectingNow(): boolean {
		return this.isReconnecting;
	}

	private handleNetworkStatusChange() {
		if (navigator.onLine) {
			// We're back online, attempt to reconnect if we have an active session
			if (this.currentStudyId && this.currentFileId && !this.isReconnecting && (!this.socket || this.socket.readyState !== WebSocket.OPEN)) {
				// message.success("Network connection restored. Reconnecting...");
				this.isReconnecting = true;
				this.immediateReconnect(this.currentStudyId, this.currentFileId);
			}
		} else {
			// We're offline, show a message only when this service owns an active session
			if (this.currentStudyId && this.currentFileId && this.socket) {
				message.error({
					content: "Network connection lost. Will reconnect when available.",
					key: "network-connection-lost",
				});
			}
		}
	}

	public injectStore(store: Store) {
		this.store = store;
	}

	private getState(): RootState {
		if (!this.store) {
			throw new Error("Store not injected. Call injectStore first.");
		}
		return this.store.getState() as RootState;
	}

	private dispatch(action: any) {
		if (!this.store) {
			throw new Error("Store not injected. Call injectStore first.");
		}
		this.store.dispatch(action);
	}

	private buildWebSocketUrl(studyId: string, fileId: string | false | null): string {
		const state = this.getState();
		const profileId = state.studiesSlice?.userProfileId;
		const roleId = state.studiesSlice?.role;
		const params = new URLSearchParams({
			study_id: studyId,
			...(fileId && { file_id: fileId }),
			...(profileId && { profile_id: profileId }),
			...(roleId && { role_id: roleId }),
		});

		return `${environment.REACT_APP_API_URL.replace(/^http/, "ws")}transcripts/edit?${params.toString()}`;
	}

	// Initialize and connect to the WebSocket
	public connect(studyId: string, fileId: string, isReconnect: boolean = false): Promise<boolean> {
		// Re-attach network listeners if a prior disconnect() removed them
		this.attachNetworkListeners();

		// Setup navigation change listener
		this.setupLocationChangeListener();

		return new Promise(async (resolve, reject) => {
			if (this.socket && this.socket.readyState === WebSocket.OPEN) {
				// If socket is already open, just resolve
				resolve(true);
				return;
			}

			this.currentStudyId = studyId;
			this.currentFileId = fileId;
			this.isReconnectedSocket = isReconnect;
			this.errorHandled = false;

			try {
				const url = this.buildWebSocketUrl(studyId, fileId);

				// For Cognito builds, pass the bearer token via `Sec-WebSocket-Protocol`
				// (the browser WebSocket API can't set a real Authorization header).
				// IDM builds rely on cookies, which the browser auto-attaches.
				const protocols = await getWebSocketAuthProtocols();
				this.socket = protocols ? new WebSocket(url, protocols) : new WebSocket(url);

				this.socket.onopen = () => {
					this.reconnectAttempts = 0;
					this.isReconnecting = false;
					this.setupHeartbeat();

					// Show success message if this was a reconnection
					if (isReconnect) {
						message.success("Connection re-established successfully");

						// Resend any pending updates
						this.resendPendingUpdates();
					}

					resolve(true);
				};

				this.socket.onmessage = (event) => {
					this.lastActivityTime = new Date();
					const data = JSON.parse(event.data);

					// Check for error about another user editing
					if (data.type === "error" && data.message?.includes("Another user is editing")) {
						this.errorHandled = true;

						// We'll reject the promise with a special error that includes the message
						const editConflictError = new Error(data.message);
						editConflictError.name = "EditConflictError";

						reject(editConflictError);
						return;
					}

					this.handleMessage(data);
				};

				this.socket.onerror = (error) => {
					console.error("WebSocket error:", error);
					if (!this.errorHandled) {
						this.isReconnecting = true;
						if (navigator.onLine) {
							// Only show connection lost message if we're online (otherwise the offline handler will show it)
							message.error("Connection lost. Attempting to reconnect...");
						}
						reject(error);
					}
				};

				this.socket.onclose = (event) => {
					const closeData = {
						type: "socket_close",
						code: event.code,
						reason: event.reason,
					};
					this.handleMessage(closeData);

					if (event.code === 1003) {
						console.error(event.reason);
						this.dispatch(setIsEditable({ isEditable: false }));

						const socketCloseError = new Error(event.reason || "Socket closed with code 1003");
						socketCloseError.name = "SocketCloseError";
						socketCloseError.code = 1003;

						// Set errorHandled flag
						this.errorHandled = true;

						// If the promise is still pending, reject it with this error
						reject(socketCloseError);
					}

					if (event.code === 1003 && event.reason?.includes("Another user is editing")) {
						message.error(event.reason);
						this.errorHandled = true;
					}

					if (!this.errorHandled && event.code !== 1000 && event.code !== 1001 && !(event.code === 1003 && event.reason?.includes("Another user is editing"))) {
						// If we're online, attempt to reconnect
						if (navigator.onLine && this.currentStudyId && this.currentFileId) {
							this.isReconnecting = true;
							this.attemptReconnect(this.currentStudyId, this.currentFileId);
						}
					} else {
						// Clean up connection resources but don't reset studyId and fileId
						this.cleanupConnection(false);
					}
				};
			} catch (error) {
				console.error("Failed to connect to WebSocket:", error);
				reject(error);
			}
		});
	}

	// Setup listener for location/navigation changes
	private setupLocationChangeListener() {
		// Remove any existing listener first
		this.removeLocationChangeListener();

		// Create a new history listener using the History API
		this.locationChangeListener = () => {
			// Disconnect the WebSocket when navigation occurs
			this.disconnect();
		};

		// Add listener for popstate event (browser back/forward)
		window.addEventListener("popstate", this.locationChangeListener);

		// Monkey patch history methods to catch programmatic navigation
		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = (...args) => {
			originalPushState.apply(history, args);
			if (this.locationChangeListener) {
				this.locationChangeListener();
			}
		};

		history.replaceState = (...args) => {
			originalReplaceState.apply(history, args);
			if (this.locationChangeListener) {
				this.locationChangeListener();
			}
		};
	}

	// Remove location change listener
	private removeLocationChangeListener() {
		if (this.locationChangeListener) {
			window.removeEventListener("popstate", this.locationChangeListener);
			this.locationChangeListener = null;

			// Restore original history methods if we modified them
			if (history.pushState.toString().includes("locationChangeListener")) {
				// This is a simplistic check - in a real app you might want to store
				// the original methods and restore them properly
				history.pushState = History.prototype.pushState;
				history.replaceState = History.prototype.replaceState;
			}
		}
	}

	// Disconnect from the WebSocket
	public disconnect() {
		if (this.socket) {
			// Send close_connection message before closing the socket
			if (this.socket.readyState === WebSocket.OPEN) {
				// Send the close_connection message
				this.send({
					type: "close_connection",
				});

				// Small delay to allow the message to be sent before closing
				setTimeout(() => {
					if (this.socket) {
						this.socket.close(1000, "Client initiated disconnect");
					}
				}, 100);
			} else {
				// If socket is not open, close it immediately
				this.socket.close(1000, "Client initiated disconnect");
			}

			// Clean up and reset studyId and fileId
			this.cleanupConnection(true);
		}

		// Close any existing connection notification
		if (this.connectionLostNotificationKey) {
			message.destroy(this.connectionLostNotificationKey);
			this.connectionLostNotificationKey = null;
		}

		// Remove navigation listener
		this.removeLocationChangeListener();

		// Remove window network listeners — re-attached on next connect()
		this.detachNetworkListeners();
	}

	// Register a message handler
	public on(messageType: string, handler: WebSocketMessageHandler) {
		if (!this.messageHandlers.has(messageType)) {
			this.messageHandlers.set(messageType, []);
		}
		this.messageHandlers.get(messageType)?.push(handler);
	}

	// Remove a message handler
	public off(messageType: string, handler: WebSocketMessageHandler) {
		if (this.messageHandlers.has(messageType)) {
			const handlers = this.messageHandlers.get(messageType) || [];
			const index = handlers.indexOf(handler);
			if (index !== -1) {
				handlers.splice(index, 1);
			}
		}
	}

	// Send a message to the WebSocket server
	public send(msg: WebSocketMessage): boolean {
		if (this.socket && this.socket.readyState === WebSocket.OPEN) {
			this.lastActivityTime = new Date();
			this.socket.send(JSON.stringify(msg));

			// Store update_transcript messages in case we need to resend after reconnection
			if (msg.type === "update_transcript") {
				this.pendingUpdates.push(msg);
			}

			return true;
		} else if (msg.type === "update_transcript") {
			// If the socket is not open but this is an update message, store it to send later
			this.pendingUpdates.push(msg);
			// If we're not already reconnecting, try to reconnect
			if (!this.isReconnecting && this.currentStudyId && this.currentFileId) {
				message.error("Connection lost while updating. Reconnecting...");
				this.immediateReconnect(this.currentStudyId, this.currentFileId);
			}
		}
		return false;
	}

	// New helper method to resend any pending transcript updates
	private resendPendingUpdates() {
		if (this.pendingUpdates.length > 0 && this.socket && this.socket.readyState === WebSocket.OPEN) {
			// Only send the most recent update to avoid conflicts
			const latestUpdate = this.pendingUpdates[this.pendingUpdates.length - 1];
			this.socket.send(JSON.stringify(latestUpdate));
		}
	}

	// Handle incoming messages
	private async handleMessage(data: WebSocketMessage) {
		const manageStudyDetails = this.getState().manageStudySlice.manageStudyDetails;

		// Handle standard message types
		if (data.type === "ping") {
			this.send({ type: "pong" });
			return;
		}

		if (data.type === "auth_refresh") {
			try {
				await getMetaDetails();
			} catch (error) {
				console.error("Error during auth refresh:", error);
			}
			return;
		}

		// Handle initial data
		if (data.type === "initial_data") {
			// Only update transcript data if it's NOT a reconnected socket
			if (data.transcript && !this.isReconnectedSocket) {
				this.dispatch(
					setTranscriptData({
						...data.transcript,
						transcripts: data.transcript.transcript || [],
					}),
				);
			}

			if (data.entity && data.entity.processed_elements) {
				this.dispatch(
					setManageStudyDetails({
						...manageStudyDetails,
						processed_elements: data.entity.processed_elements || {},
					}),
				);
			}

			// Reset the reconnected socket flag after handling initial data
			this.isReconnectedSocket = false;
		}

		if (data.type === "entity_data_update") {
			if (data.entity && data.entity.processed_elements) {
				this.dispatch(
					setManageStudyDetails({
						...manageStudyDetails,
						processed_elements: data.entity.processed_elements || {},
					}),
				);
			}
		}

		if (data.type === "transcript_data") {
			if (data.transcript) {
				this.dispatch(setTranscriptData({ ...data.transcript, details: data.transcript.upload_details, transcripts: data?.transcript.transcript }));
			}
		}

		// Handle error messages
		if (data.type === "error") {
			console.error("WebSocket error from server:", data.message);
			message.error(data.message);
			return;
		}

		// Handle transcript update from server
		if (data.type === "transcript_update") {
			if (data.transcript) {
				this.dispatch(
					setTranscriptData({
						...data.transcript,
						transcripts: data.transcript.transcript || [],
					}),
				);
			}
		}

		// Handle transcript update confirmation from server
		if (data.type === "update_confirmation") {
			this.dispatch(setUnsavedChanges(false));

			// Clear pending updates when server confirms the update
			this.pendingUpdates = [];
		}

		// Handle revert confirmation
		if (data.type === "revert_confirmation") {
			if (data.transcript) {
				message.success(data.message || "Transcript revert successfully!");
				this.dispatch(
					setTranscriptData({
						...data.transcript,
						details: data.transcript.upload_details,
						transcripts: data.transcript.transcript || [],
					}),
				);
				this.dispatch(setUnsavedChanges(false));
			}
		}

		// Dispatch to registered handlers
		const handlers = this.messageHandlers.get(data.type) || [];
		handlers.forEach((handler) => {
			try {
				handler(data);
			} catch (error) {
				console.error(`Error in handler for message type "${sanitizeForLog(data.type)}":`, error);
			}
		});
	}

	// Setup heartbeat mechanism
	private setupHeartbeat() {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
		}

		// Check connection every 15 seconds
		this.heartbeatInterval = setInterval(() => {
			if (this.socket && this.socket.readyState === WebSocket.OPEN) {
				// Check inactivity (28 minutes - just under the server's 30 minute timeout)
				const idleTime = (new Date().getTime() - this.lastActivityTime.getTime()) / 1000;
				if (idleTime > 1680) {
					// 28 minutes
					// Send a ping to keep the connection alive
					this.send({ type: "ping", timestamp: new Date().toISOString() });
				}
			} else if (this.socket && this.socket.readyState === WebSocket.CLOSED && !this.isReconnecting) {
				// Socket closed but not in reconnecting state, attempt to reestablish
				if (this.currentStudyId && this.currentFileId && navigator.onLine) {
					this.isReconnecting = true;
					this.attemptReconnect(this.currentStudyId, this.currentFileId);
				}
			}
		}, 15000); // 15 seconds
	}

	// Cleanup connection resources
	private cleanupConnection(resetIds: boolean = true) {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}

		if (this.reconnectTimeout) {
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}

		// If resetIds is true, clear the current session identifiers and pending updates
		if (resetIds) {
			this.currentStudyId = null;
			this.currentFileId = null;
			this.isReconnecting = false;
			this.pendingUpdates = [];
		}
	}

	public handlePageLoadReconnection() {
		const manageStudyDetails = this.getState().manageStudySlice.manageStudyDetails;
		const file_id = this.getState().manageTranscriptSlice.file_id;
		this.currentStudyId = manageStudyDetails._id;
		this.currentFileId = file_id;
		this.immediateReconnect(this.currentStudyId, this.currentFileId);
	}

	// Immediate reconnect attempt (for when network comes back online)
	public immediateReconnect(studyId: string, fileId: string) {
		// Don't attempt to reconnect if we're offline
		// if (!navigator.onLine) {
		// 	this.isReconnecting = false;
		// 	return;
		// }

		// Don't reconnect if we're already connecting or connected
		if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
			this.isReconnecting = false;
			return;
		}

		this.isReconnecting = true;

		// If we have a socket, but it's closed, create a new one
		if (this.socket) {
			// Close the existing socket if it's not already closed
			if (this.socket.readyState !== WebSocket.CLOSED) {
				this.socket.close();
			}
			this.socket = null;
		}

		// Try to establish a new connection immediately with the reconnect flag
		this.connect(studyId, fileId, true)
			.then(() => {
				this.isReconnecting = false;
			})
			.catch((error) => {
				console.error("Immediate reconnection failed:", error);
				// If immediate reconnection fails, fall back to the regular retry schedule
				if (navigator.onLine) {
					this.attemptReconnect(studyId, fileId);
				} else {
					this.isReconnecting = false;
				}
			});
	}

	// Attempt to reconnect with scheduled retries
	private attemptReconnect(studyId: string, fileId: string) {
		// Don't attempt to reconnect if we're offline
		// if (!navigator.onLine) {
		// 	this.isReconnecting = false;
		// 	return;
		// }

		// If max attempts not reached yet
		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			// Clear any existing timeout
			if (this.reconnectTimeout) {
				clearTimeout(this.reconnectTimeout);
			}

			const delay = 15000; // 15 seconds

			this.reconnectTimeout = setTimeout(() => {
				this.reconnectAttempts++;

				// If we have a socket, but it's closed, create a new one
				if (this.socket) {
					// Close the existing socket if it's not already closed
					if (this.socket.readyState !== WebSocket.CLOSED) {
						this.socket.close();
					}
					this.socket = null;
				}

				// Try to establish a new connection with the reconnect flag
				this.connect(studyId, fileId, true)
					.then(() => {
						this.isReconnecting = false;
					})
					.catch((error) => {
						console.error("Reconnection attempt failed:", error);
						// If still online, schedule another attempt
						if (navigator.onLine) {
							this.attemptReconnect(studyId, fileId);
						} else {
							this.isReconnecting = false;
						}
					});
			}, delay);
		} else {
			console.error("Max reconnection attempts reached (30 minutes).");
			this.isReconnecting = false;
			message.error("Failed to reconnect after multiple attempts. Please refresh the page.");
		}
	}

	// Modified initializeWithInitialData method to handle socket close events
	public initializeWithInitialData(studyId: string, fileId: string): Promise<any> {
		return new Promise((resolve, reject) => {
			// Create a one-time message handler for initial data or error
			const handleInitialMessage = (data) => {
				if (data.type === "initial_data") {
					// Remove this one-time listener
					this.off("initial_data", handleInitialMessage);
					this.off("error", handleErrorMessage);
					this.off("socket_close", handleSocketClose);
					resolve(data);
				}
			};

			const handleErrorMessage = (data) => {
				if (data.type === "error") {
					// Remove listeners
					this.off("initial_data", handleInitialMessage);
					this.off("error", handleErrorMessage);
					this.off("socket_close", handleSocketClose);
					reject(new Error(data.message));
				}
			};

			// New handler for socket close events
			const handleSocketClose = (data) => {
				if (data.code === 1003) {
					// Remove listeners
					this.off("initial_data", handleInitialMessage);
					this.off("error", handleErrorMessage);
					this.off("socket_close", handleSocketClose);

					// Create specific error for code 1003
					const socketCloseError = new Error(data.reason || "Socket connection closed with code 1003");
					socketCloseError.name = "SocketCloseError";
					socketCloseError.code = 1003;

					reject(socketCloseError);
				}
			};

			// Register our one-time handlers
			this.on("initial_data", handleInitialMessage);
			this.on("error", handleErrorMessage);
			this.on("socket_close", handleSocketClose);

			// Start the connection
			this.connect(studyId, fileId).catch((err) => {
				// If connection fails, clean up listeners and reject
				this.off("initial_data", handleInitialMessage);
				this.off("error", handleErrorMessage);
				this.off("socket_close", handleSocketClose);
				reject(err);
			});
		});
	}

	// Update transcript
	public updateTranscript(data: any): boolean {
		if (!this.currentStudyId || !this.currentFileId) {
			console.error("No active editing session");
			return false;
		}

		return this.send({
			type: "update_transcript",
			data,
		});
	}

	// Switch transcript
	public switchTranscript(newFileId: string, is_edit_mode: boolean): boolean {
		if (!this.currentStudyId) {
			console.error("No active study ID");
			return false;
		}

		this.currentFileId = newFileId;

		return this.send({
			type: "switch_transcript",
			file_id: newFileId,
			is_edit_mode: is_edit_mode,
		});
	}

	// Revert transcript
	public revertTranscript(documentId: string): boolean {
		if (!this.currentStudyId || !this.currentFileId) {
			console.error("No active editing session");
			return false;
		}

		return this.send({
			type: "revert_transcript",
			document_id: documentId,
			file_id: this.currentFileId,
		});
	}
}

// Create a singleton instance
const websocketTranscriptService = new WebSocketTranscriptService();

export default websocketTranscriptService;
